import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, X, Filter, RotateCcw } from 'lucide-react';
import { User } from '@/types';

interface AdvancedUserSearchProps {
  onSearch: (users: User[]) => void;
  allUsers: User[];
}

interface SearchFilters {
  name: string;
  email: string;
  jobTitle: string;
  school: string;
  department: string;
  employmentStatus: string;
  jobType: string;
}

export default function AdvancedUserSearch({ onSearch, allUsers }: AdvancedUserSearchProps) {
  const [filters, setFilters] = useState<SearchFilters>({
    name: '',
    email: '',
    jobTitle: '',
    school: '',
    department: '',
    employmentStatus: 'all',
    jobType: 'all',
  });
  
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [resultCount, setResultCount] = useState(allUsers.length);

  // Extract unique values for dropdowns
  const uniqueSchools = [...new Set(allUsers.map(u => u.school_name).filter(Boolean))].sort();
  const uniqueDepartments = [...new Set(allUsers.map(u => u.department).filter(Boolean))].sort();
  const uniqueJobTypes = [...new Set(allUsers.map(u => u.job_type).filter(Boolean))].sort();
  const uniqueStatuses = [...new Set(allUsers.map(u => u.employment_status).filter(Boolean))].sort();

  const handleFilterChange = (key: keyof SearchFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    let filtered = [...allUsers];
    const active: string[] = [];

    if (filters.name.trim()) {
      const searchName = filters.name.toLowerCase();
      filtered = filtered.filter(u => 
        u.full_name?.toLowerCase().includes(searchName) ||
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(searchName)
      );
      active.push(`Name: "${filters.name}"`);
    }

    if (filters.email.trim()) {
      const searchEmail = filters.email.toLowerCase();
      filtered = filtered.filter(u => u.email?.toLowerCase().includes(searchEmail));
      active.push(`Email: "${filters.email}"`);
    }

    if (filters.jobTitle.trim()) {
      const searchTitle = filters.jobTitle.toLowerCase();
      filtered = filtered.filter(u => u.job_title?.toLowerCase().includes(searchTitle));
      active.push(`Job Title: "${filters.jobTitle}"`);
    }

    if (filters.school.trim()) {
      const searchSchool = filters.school.toLowerCase();
      filtered = filtered.filter(u => u.school_name?.toLowerCase().includes(searchSchool));
      active.push(`School: "${filters.school}"`);
    }

    if (filters.department.trim()) {
      const searchDept = filters.department.toLowerCase();
      filtered = filtered.filter(u => u.department?.toLowerCase().includes(searchDept));
      active.push(`Department: "${filters.department}"`);
    }

    if (filters.employmentStatus && filters.employmentStatus !== 'all') {
      filtered = filtered.filter(u => u.employment_status === filters.employmentStatus);
      active.push(`Status: ${filters.employmentStatus}`);
    }

    if (filters.jobType && filters.jobType !== 'all') {
      filtered = filtered.filter(u => u.job_type === filters.jobType);
      active.push(`Job Type: ${filters.jobType}`);
    }

    setActiveFilters(active);
    setResultCount(filtered.length);
    onSearch(filtered);
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      email: '',
      jobTitle: '',
      school: '',
      department: '',
      employmentStatus: 'all',
      jobType: 'all',
    });
    setActiveFilters([]);
    setResultCount(allUsers.length);
    onSearch(allUsers);
  };

  const removeFilter = (filterText: string) => {
    // Parse the filter text and reset that specific filter
    const [key] = filterText.split(':');
    const filterKey = key.toLowerCase().trim();
    
    const newFilters = { ...filters };
    if (filterKey === 'name') newFilters.name = '';
    if (filterKey === 'email') newFilters.email = '';
    if (filterKey === 'job title') newFilters.jobTitle = '';
    if (filterKey === 'school') newFilters.school = '';
    if (filterKey === 'department') newFilters.department = '';
    if (filterKey === 'status') newFilters.employmentStatus = 'all';
    if (filterKey === 'job type') newFilters.jobType = 'all';
    
    setFilters(newFilters);
  };

  // Update result count when allUsers changes
  useEffect(() => {
    setResultCount(allUsers.length);
  }, [allUsers.length]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Advanced Search
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-sm">
                {resultCount} user{resultCount !== 1 ? 's' : ''} found
              </Badge>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Text Search Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search-name">Name</Label>
              <Input
                id="search-name"
                placeholder="Search by name..."
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="search-email">Email</Label>
              <Input
                id="search-email"
                placeholder="Search by email..."
                value={filters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="search-job-title">Job Title</Label>
              <Input
                id="search-job-title"
                placeholder="Search by job title..."
                value={filters.jobTitle}
                onChange={(e) => handleFilterChange('jobTitle', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="search-school">School/Site</Label>
              <Input
                id="search-school"
                placeholder="Search by school..."
                value={filters.school}
                onChange={(e) => handleFilterChange('school', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              />
            </div>
          </div>

          {/* Dropdown Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Department</Label>
              <Select 
                value={filters.department || 'all_depts'} 
                onValueChange={(v) => handleFilterChange('department', v === 'all_depts' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_depts">All Departments</SelectItem>
                  {uniqueDepartments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Employment Status</Label>
              <Select 
                value={filters.employmentStatus} 
                onValueChange={(v) => handleFilterChange('employmentStatus', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                  {uniqueStatuses.filter(s => !['active', 'inactive', 'pending', 'terminated'].includes(s)).map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Job Type</Label>
              <Select 
                value={filters.jobType} 
                onValueChange={(v) => handleFilterChange('jobType', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All job types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Job Types</SelectItem>
                  <SelectItem value="teacher">Teacher</SelectItem>
                  <SelectItem value="administrator">Administrator</SelectItem>
                  <SelectItem value="support_staff">Support Staff</SelectItem>
                  <SelectItem value="substitute">Substitute</SelectItem>
                  <SelectItem value="para_professional">Para Professional</SelectItem>
                  {uniqueJobTypes.filter(t => !['teacher', 'administrator', 'support_staff', 'substitute', 'para_professional'].includes(t)).map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
            <div className="flex flex-wrap gap-2">
              {activeFilters.map((filter, idx) => (
                <Badge 
                  key={idx} 
                  variant="secondary" 
                  className="flex items-center gap-1 px-3 py-1"
                >
                  {filter}
                  <button
                    onClick={() => {
                      removeFilter(filter);
                      // Re-apply remaining filters
                      setTimeout(applyFilters, 0);
                    }}
                    className="ml-1 hover:bg-muted rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={clearFilters}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <Button onClick={applyFilters}>
                <Filter className="h-4 w-4 mr-2" />
                Apply Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}